import { FC } from "react";
import { X } from "lucide-react";
import { chipColor } from "../../../utils/colorUtils";
import { cx } from "../../../utils/cx";
import styles from "./PunchupTag.module.css";

export interface PunchupTagProps {
  label: string;
  /** Tag color as a 6-digit hex string (e.g. '#3B82F6'). Drives background tint, border, and text. */
  background?: string;
  /** If provided, renders a remove (×) button inside the chip. */
  onRemove?: () => void;
  /** If provided, the entire chip becomes a clickable button. Hover matches .punchupButtonOutline. */
  onClick?: () => void;
  /**
   * Size variant. `default` is the chip used in tag-management surfaces
   * (Event Tags accordion, ticket-tags field, etc.). `sm` shrinks font and
   * padding for table-cell density — use for status / type / scanned-style
   * categorical cells where the chip sits inside a tight row.
   */
  size?: "default" | "sm";
  /**
   * Colour treatment. `tint` is the soft-label chip — a translucent `color-mix`
   * fill and a 55%-opacity border. `solid` is the qualifier badge the design
   * draws beside a label: an opaque tint over the page
   * background and a full-strength border. At `sm` it also takes the badge's
   * regular weight and wider pill padding.
   */
  appearance?: "tint" | "solid";
  /** Optional accessible-only description; rendered as a `title` attribute on the chip for hover tooltip support. */
  title?: string;
}

export const PunchupTag: FC<PunchupTagProps> = ({
  label,
  background = "#FF5E2A",
  onRemove,
  onClick,
  size = "default",
  appearance = "tint",
  title,
}) => {
  const chipStyle = {
    "--chip-color": chipColor(background),
  } as React.CSSProperties;
  const sizeClass = size === "sm" ? styles.tagSmall : "";
  const appearanceClass = appearance === "solid" ? styles.tagSolid : "";

  // Hybrid mode: both edit (onClick) and direct remove (onRemove) coexist.
  // <span> container avoids nested <button> elements (invalid HTML).
  if (onClick && onRemove) {
    return (
      <span
        className={cx(
          styles.tag,
          styles.tagClickable,
          sizeClass,
          appearanceClass
        )}
        style={chipStyle}
        title={title}
        data-size={size}
        data-appearance={appearance}
      >
        <span
          className={styles.labelButton}
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              // preventDefault on Space: a span[role="button"] does not suppress the browser's default
              // Space scroll the way a native <button> does, so without this the page scrolls on activate.
              e.preventDefault();
              onClick();
            }
          }}
        >
          {label}
        </span>
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={`Remove ${label}`}
        >
          <X size={18} strokeWidth={2} />
        </button>
      </span>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={cx(
          styles.tag,
          styles.tagClickable,
          sizeClass,
          appearanceClass
        )}
        style={chipStyle}
        onClick={onClick}
        title={title}
        data-size={size}
        data-appearance={appearance}
      >
        <span className={styles.label}>{label}</span>
      </button>
    );
  }

  return (
    <span
      className={cx(styles.tag, sizeClass, appearanceClass)}
      style={chipStyle}
      title={title}
      data-size={size}
      data-appearance={appearance}
    >
      <span className={styles.label}>{label}</span>
      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={`Remove ${label}`}
        >
          <X size={18} strokeWidth={2} />
        </button>
      )}
    </span>
  );
};
